import { Radio } from 'antd'
import { useState } from 'react'

import { Volume } from '@/api/type-server'
import { Button, Input, Modal, Trans } from '@/components'

import { VolumeSelector } from './VolumeSelector'

export interface MountDriveModalProps<T extends Volume = Volume> {
  open: boolean
  mountVolume?: T
  mountPath?: string
  readWriteMode?: boolean
  /** Backend may only support read & write mounts (e.g. team instances); false greys out "Read only". */
  allowReadOnly?: boolean
  volumes?: T[]
  machineDomains?: number[]
  canWriteVolume: boolean
  getVolumeQuota: (volume: T) => number
  titleI18nKey?: string
  titleDefault?: string
  onChange: (volume: T, path: string, mode: boolean) => void
  onClose: () => void
}

export function MountDriveModal<T extends Volume = Volume>(props: MountDriveModalProps<T>) {
  const {
    open,
    volumes,
    mountVolume: defaultMountVolume,
    mountPath: defaultMountPath,
    machineDomains,
    canWriteVolume,
    getVolumeQuota,
    titleI18nKey = 'create.mountDriveModalTitle',
    titleDefault = 'Mount Datadrive',
    readWriteMode: defaultReadWriteMode,
    allowReadOnly = true,
    onChange,
    onClose,
  } = props

  const [mountVolume, setMountVolume] = useState<T | undefined>(defaultMountVolume)
  const [mountPath] = useState<string | undefined>(defaultMountPath)
  const [readWriteMode, setReadWriteMode] = useState<boolean>(
    !canWriteVolume || (allowReadOnly && (defaultReadWriteMode ?? false)),
  )

  const onMountVolumeSelected = (volume: T | undefined) => {
    setMountVolume(volume)
  }

  const onOK = () => {
    onChange && onChange(mountVolume!, mountPath!, readWriteMode)
  }

  return (
    <Modal
      className="w-[800px]"
      open={open}
      onClose={() => {
        onClose && onClose()
      }}
    >
      <Modal.Header>
        <Trans i18nKey={titleI18nKey}>{titleDefault}</Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="">
          <VolumeSelector<T>
            values={volumes}
            value={mountVolume}
            machineDomains={machineDomains}
            getVolumeQuota={getVolumeQuota}
            onChange={onMountVolumeSelected}
          />
          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="create.mountDriveModalMountPath">Mount Path</Trans>
            </h3>
            <Input type="text" value={mountPath} maxLength={30} disabled={true} />
          </div>
          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="create.mountDriveModalPermissions">Permissions</Trans>
            </h3>
            <Radio.Group
              value={readWriteMode}
              onChange={(e) => setReadWriteMode(e.target.value)}
              disabled={!canWriteVolume}
            >
              <Radio value={true} disabled={!allowReadOnly}>
                <Trans i18nKey="create.mountDriveModalPermissionReadOnly">Read only</Trans>
              </Radio>
              <Radio value={false} disabled={!canWriteVolume}>
                <Trans i18nKey="create.mountDriveModalPermissionReadWrite">Read & write</Trans>
              </Radio>
            </Radio.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            onClose && onClose()
          }}
        >
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={!mountVolume || !mountPath || mountPath.length <= 0}
          onClick={onOK}
        >
          <Trans i18nKey="create.mountAsANetworkDrive">Mount</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
